import React, { useState, useEffect } from 'react';
import '../../assets/styles/2025/form.css';
import { FormData, formValidator, ValidationError } from '../../services/formValidation';
import { applicationService } from '../../services/applicationService';
import { AWARD_CATEGORIES } from '../../config/2025/appConfig';
import FormError from '../../components/FormError';
import LoadingSpinner from '../../components/LoadingSpinner';
import SuccessMessage from '../../components/SuccessMessage';


const Form2025: React.FC = () => {
  // Application deadline: September 19, 2025 at 10:00 AM
  const APPLICATION_END_DAY = new Date('2025-09-19T10:00:00+08:00'); // Hong Kong time

  // Check if application deadline has passed
  const isApplicationClosed = () => {
    return new Date() > APPLICATION_END_DAY;
  };

  // State management for form data
  const [formData, setFormData] = useState<FormData>({
    // Entry Information
    entry_title_eng: '',
    entry_title_chi: '',
    entry_info: '',

    // Company Information
    company_name_eng: '',
    company_name_chi: '',
    company_info: '',
    company_address: '',
    company_ceo_name: '',
    company_business: '',
    company_url: 'http://',
    company_registration_no: '',
    company_incorporation_no: '',
    company_incorporation_date: '',
    startup: '',
    company_employee_no: '',
    entry_incubation: '',
    entry_incubation_description: '',
    company_logo_1: null,
    company_logo_2: null,
    corporate_guideline: null,

    // Contact Details
    contact_name_1: '',
    contact_title_1: '',
    contact_phone_1: '852',
    contact_email_1: '',
    contact_name_2: '',
    contact_title_2: '',
    contact_phone_2: '852',
    contact_email_2: '',

    // Award Category
    award_category: '',

    // Judging Materials
    judging_specialfeature_1: '',
    judging_specialfeature_2: '',
    judging_specialfeature_3: '',
    judging_specialfeature_4: '',
    judging_specialfeature_5: '',
    judging_specialfeature_6: '',
    entry_photo: null,
    judging_doc_material: null,
    judging_video_material_1: '',

  // Declaration
    about_submission_usage: false,
    about_application: false,
    entry_patent: '',
    entry_patent_number: '',
    entry_patent_title: '',
    entry_patent_proprietor: '',
    entry_patent_country: '',
    entry_patent_date: '',
    entry_patent_status: '',
    entry_dispute: '',
    entry_dispute_description: '',
    event_cost: false,
    data_policy_1: false,
    data_policy_2: false,

    // Captcha
    checknum: ''
  });

  // State for conditional field visibility
  const [showPatentSection, setShowPatentSection] = useState(false);
  const [showDisputeDescription, setShowDisputeDescription] = useState(false);
  const [showIncubationDescription, setShowIncubationDescription] = useState(false);

  // State for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [submissionError, setSubmissionError] = useState<string>('');

  // State for captcha
  const [captchaCode, setCaptchaCode] = useState('');

  // Generate random captcha code
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(result);
    return result;
  };

  // Initialize captcha on component mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  //// console.log('Form component is rendering...');

  // Handle input changes with real-time validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear validation errors for this field
    setValidationErrors(prev => prev.filter(error => error.field !== name));

    // Real-time validation for specific fields
    const fieldValue = type === 'checkbox' ? checked : value;
    const validationError = formValidator.validateField(name as keyof FormData, fieldValue);

    if (validationError) {
      setValidationErrors(prev => [...prev, validationError]);
    }

    // Handle conditional field visibility
    if (name === 'entry_patent') {
      setShowPatentSection(value === 'Y');
    }
    if (name === 'entry_dispute') {
      setShowDisputeDescription(value === 'Y');
    }
    if (name === 'entry_incubation') {
      setShowIncubationDescription(value === 'O');
    }
  };

  // Handle file uploads
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData(prev => ({
          ...prev,
        [name]: files[0]
      }));
    }
  };

  // Function to scroll to the first error field
  const scrollToFirstError = (errors: ValidationError[]) => {
    if (errors.length > 0) {
      const firstError = errors[0];
      let errorElement: Element | null = null;

      // Special handling for supplementary_materials validation
      if (firstError.field === 'supplementary_materials') {
        errorElement = document.querySelector('.entry_supplementary');
      } else {
        errorElement = document.querySelector(`[name="${firstError.field}"]`);
      }

      if (errorElement) {
        // Scroll to the element with smooth animation
        errorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        // Focus on the element for better UX (only if it's an input element)
        if (errorElement instanceof HTMLElement && (errorElement.tagName === 'INPUT' || errorElement.tagName === 'TEXTAREA' || errorElement.tagName === 'SELECT')) {
          errorElement.focus();
        }
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear any previous errors
    setSubmissionError('');

    // Validate captcha first
    if (formData.checknum.toUpperCase() !== captchaCode) {
      setSubmissionError('驗證碼不正確，請重新輸入。\nVerification code is incorrect, please re-enter.');
      generateCaptcha(); // Generate new captcha

      // Scroll to captcha field
      setTimeout(() => {
        const captchaElement = document.querySelector('[name="checknum"]');
        if (captchaElement) {
          captchaElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          (captchaElement as HTMLElement).focus();
        }
      }, 100);

      return;
    }

    // Validate form
    const errors = formValidator.validateForm(formData);
    if (errors.length > 0) {
      setValidationErrors(errors);
      setSubmissionError('Please correct the errors in the form before submitting.');

      // Scroll to the first error after a short delay to ensure state is updated
      setTimeout(() => {
        scrollToFirstError(errors);
      }, 100);

      return;
    }

    setIsSubmitting(true);

    try {
      const result = await applicationService.submitApplication(formData);

      if (result.success) {
        setShowSuccess(true);
        // Reset form
        setFormData({
          entry_title_eng: '',
          entry_title_chi: '',
          entry_info: '',
          company_name_eng: '',
          company_name_chi: '',
          company_info: '',
          company_address: '',
          company_ceo_name: '',
          company_business: '',
          company_url: 'http://',
          company_registration_no: '',
          company_incorporation_no: '',
          company_incorporation_date: '',
          startup: '',
          company_employee_no: '',
          entry_incubation: '',
          entry_incubation_description: '',
          company_logo_1: null,
          company_logo_2: null,
          corporate_guideline: null,
          contact_name_1: '',
          contact_title_1: '',
          contact_phone_1: '852',
          contact_email_1: '',
          contact_name_2: '',
          contact_title_2: '',
          contact_phone_2: '852',
          contact_email_2: '',
          award_category: '',
          judging_specialfeature_1: '',
          judging_specialfeature_2: '',
          judging_specialfeature_3: '',
          judging_specialfeature_4: '',
          judging_specialfeature_5: '',
          judging_specialfeature_6: '',
          entry_photo: null,
          judging_doc_material: null,
          judging_video_material_1: '',
          about_submission_usage: false,
          about_application: false,
          entry_patent: '',
          entry_patent_number: '',
          entry_patent_title: '',
          entry_patent_proprietor: '',
          entry_patent_country: '',
          entry_patent_date: '',
          entry_patent_status: '',
          entry_dispute: '',
          entry_dispute_description: '',
          event_cost: false,
          data_policy_1: false,
          data_policy_2: false,
          checknum: ''
        });
        setShowPatentSection(false);
        setShowDisputeDescription(false);
        setShowIncubationDescription(false);
      } else {
        setSubmissionError(result.message);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmissionError('An error occurred while submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Test data auto-fill function with random selections
  const fillTestData = () => {
    // Random company names and Chinese translations
    const companyNames = [
      { eng: 'TechVision Solutions Limited', chi: '科技視野解決方案有限公司' },
      { eng: 'SmartCity Innovations Ltd', chi: '智慧城市創新有限公司' },
      { eng: 'Digital Future Technologies', chi: '數碼未來科技有限公司' },
      { eng: 'GreenTech Solutions HK', chi: '綠色科技解決方案香港' },
      { eng: 'AI Connect Systems Limited', chi: '人工智能連接系統有限公司' },
      { eng: 'Urban Smart Platform Co.', chi: '城市智慧平台公司' },
      { eng: 'EcoTech Development Ltd', chi: '生態科技發展有限公司' },
      { eng: 'DataFlow Analytics Limited', chi: '數據流分析有限公司' },
      { eng: 'MobileFirst Solutions', chi: '移動優先解決方案' },
      { eng: 'CloudBridge Technologies', chi: '雲橋科技有限公司' },
      { eng: 'IoT Master Systems Ltd', chi: '物聯網大師系統有限公司' },
      { eng: 'Blockchain Future HK', chi: '區塊鏈未來香港' },
      { eng: 'HealthTech Innovations', chi: '健康科技創新' },
      { eng: 'EduSmart Solutions Ltd', chi: '教育智慧解決方案有限公司' },
      { eng: 'FinTech Connect Limited', chi: '金融科技連接有限公司' },
      { eng: 'PropertyTech Systems', chi: '房地產科技系統' },
      { eng: 'WearableTech Solutions', chi: '可穿戴科技解決方案' }
    ];

    // Random entry titles and Chinese translations
    const entryTitles = [
      { eng: 'Smart City IoT Platform for Urban Management', chi: '智慧城市物聯網管理平台' },
      { eng: 'AI-Powered Healthcare Monitoring System', chi: '人工智能驅動的醫療監控系統' },
      { eng: 'Green Energy Management Platform', chi: '綠色能源管理平台' },
      { eng: 'Digital Payment Security Solution', chi: '數字支付安全解決方案' },
      { eng: 'Educational Technology Learning Platform', chi: '教育科技學習平台' },
      { eng: 'ESG Compliance Management System', chi: 'ESG合規管理系統' },
      { eng: 'Geron Technology Care Platform', chi: '樂齡科技護理平台' },
      { eng: 'Marketing Technology Analytics Suite', chi: '行銷科技分析套件' },
      { eng: 'Property Management Smart System', chi: '物業管理智慧系統' },
      { eng: 'SME Digital Transformation Platform', chi: '中小企業數碼轉型平台' },
      { eng: 'Environmental Monitoring Network', chi: '環境監測網絡' },
      { eng: 'Government Digital Services Portal', chi: '政府數碼服務門戶' },
      { eng: 'Smart Mobility Transportation System', chi: '智慧出行交通系統' },
      { eng: 'Workforce Management AI Platform', chi: '勞動力管理人工智能平台' },
      { eng: 'Wearable IoT Health Monitor', chi: '可穿戴物聯網健康監測器' },
      { eng: 'Metaverse Virtual Learning Environment', chi: '元宇宙虛擬學習環境' },
      { eng: 'Blockchain Supply Chain Tracker', chi: '區塊鏈供應鏈追蹤器' },
      { eng: 'Smart Building Energy Optimizer', chi: '智能建築能源優化器' }
    ];

    // Random company descriptions
    const companyDescriptions = [
      'A leading technology company specializing in smart city solutions and IoT platforms. Founded in 2018, we focus on developing innovative technologies that improve urban living through digital transformation.',
      'Pioneering digital solutions for the future of urban development. Our team combines expertise in AI, IoT, and sustainable technology to create impactful solutions.',
      'Dedicated to transforming traditional industries through cutting-edge technology. We specialize in creating scalable solutions that address real-world challenges.',
      'Committed to environmental sustainability through innovative technology solutions. Our green tech initiatives help organizations reduce their carbon footprint.',
      'At the forefront of artificial intelligence and machine learning applications. We develop intelligent systems that enhance productivity and decision-making.',
      'Revolutionizing urban infrastructure through smart technology integration. Our platforms provide real-time insights for better city management.',
      'Focused on creating sustainable and eco-friendly technology solutions. We believe in technology that benefits both people and the planet.',
      'Experts in data analytics and business intelligence. We transform raw data into actionable insights for strategic decision-making.',
      'Mobile-first approach to technology solutions. We create responsive and user-friendly applications that work seamlessly across all devices.',
      'Building bridges between cloud technologies and business needs. Our cloud solutions enable organizations to scale efficiently and securely.',
      'Masters of IoT system integration and management. We connect devices, collect data, and create intelligent automation solutions.',
      'Pioneering blockchain technology for secure and transparent business processes. We build trust through decentralized solutions.',
      'Innovating healthcare technology for better patient outcomes. Our solutions improve medical care delivery and patient monitoring.',
      'Transforming education through technology. We create engaging learning experiences that adapt to individual student needs.',
      'Connecting financial services with cutting-edge technology. Our fintech solutions streamline banking and payment processes.',
      'Revolutionizing property management through smart technology. We create efficient and sustainable building management systems.',
      'Leading the wearable technology revolution. Our devices monitor health and provide real-time insights for better wellness.',
      'Creating immersive virtual environments for learning and collaboration. Our metaverse solutions open new possibilities for digital interaction.'
    ];

    // Random selection functions
    const getRandomItem = <T,>(array: readonly T[]): T => {
      return array[Math.floor(Math.random() * array.length)];
    };

    const getRandomAwardCategory = () => {
      return getRandomItem(AWARD_CATEGORIES).value;
    };

    const getRandomCompany = () => getRandomItem(companyNames);
    const getRandomEntryTitle = () => getRandomItem(entryTitles);
    const getRandomDescription = () => getRandomItem(companyDescriptions);

    // Generate random company registration number
    const generateRandomRegNo = () => {
      const numbers = '0123456789';
      let result = 'BR';
      for (let i = 0; i < 8; i++) {
        result += numbers.charAt(Math.floor(Math.random() * numbers.length));
      }
      return result;
    };

    // Generate random incorporation number
    const generateRandomIncNo = () => {
      const numbers = '0123456789';
      let result = '';
      for (let i = 0; i < 7; i++) {
        result += numbers.charAt(Math.floor(Math.random() * numbers.length));
      }
      return result;
    };

    // Generate random date within last 5 years
    const generateRandomDate = () => {
      const start = new Date(2019, 0, 1);
      const end = new Date();
      const randomDate = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
      return randomDate.toISOString().split('T')[0];
    };

    // Generate random phone number
    const generateRandomPhone = () => {
      const numbers = '0123456789';
      let result = '852-';
      for (let i = 0; i < 4; i++) {
        result += numbers.charAt(Math.floor(Math.random() * numbers.length));
      }
      result += '-';
      for (let i = 0; i < 4; i++) {
        result += numbers.charAt(Math.floor(Math.random() * numbers.length));
      }
      return result;
    };

    // Get random selections
    const selectedCompany = getRandomCompany();
    const selectedEntryTitle = getRandomEntryTitle();
    const selectedCategory = getRandomAwardCategory();
    const selectedDescription = getRandomDescription();

    setFormData({
      // Entry Information
      entry_title_eng: selectedEntryTitle.eng,
      entry_title_chi: selectedEntryTitle.chi,
      entry_info: `An innovative ${selectedCategory} solution that leverages cutting-edge technology to address modern challenges. ${selectedDescription} This platform demonstrates exceptional innovation and practical value for end users.`,

      // Company Information
      company_name_eng: selectedCompany.eng,
      company_name_chi: selectedCompany.chi,
      company_info: selectedDescription,
      company_address: 'Unit 2501, 25/F, Cyberport 3, 100 Cyberport Road, Hong Kong',
      company_ceo_name: 'John Chen',
      company_business: 'Software Development and Smart City Solutions',
      company_url: 'https://www.techvision.com.hk',
      company_registration_no: generateRandomRegNo(),
      company_incorporation_no: generateRandomIncNo(),
      company_incorporation_date: generateRandomDate(),
      startup: 'Y',
      company_employee_no: '51_to_100',
      entry_incubation: 'C',
      entry_incubation_description: 'We are part of Cyberport Incubation Programme since 2019, which has provided us with valuable mentorship and resources.',
      company_logo_1: null,
      company_logo_2: null,
      corporate_guideline: null,

      // Contact Details - Updated with wslo@etnet.com.hk
      contact_name_1: 'Wilson Lo',
      contact_title_1: 'Chief Technology Officer',
      contact_phone_1: generateRandomPhone(),
      contact_email_1: 'wslo@etnet.com.hk',
      contact_name_2: 'Wilson Lo',
      contact_title_2: 'Project Manager',
      contact_phone_2: generateRandomPhone(),
      contact_email_2: 'wslo@etnet.com.hk',

      // Award Category - Randomly selected
      award_category: selectedCategory,

      // Judging Materials
      judging_specialfeature_1: `Our ${selectedCategory} solution introduces revolutionary machine learning algorithms that can predict and optimize performance, reducing operational costs by 40% and improving efficiency.`,
      judging_specialfeature_2: 'The user interface is designed with accessibility in mind, featuring intuitive dashboards that can be easily operated by users with minimal training. Mobile app integration allows real-time monitoring.',
      judging_specialfeature_3: 'Built with enterprise-grade security and scalability, the platform has undergone rigorous testing and is certified ISO 27001. It can handle high-volume data processing simultaneously.',
      judging_specialfeature_4: 'Our solution has already shown measurable benefits in pilot projects: 25% improvement in performance, 30% reduction in errors, and 20% increase in user satisfaction.',
      judging_specialfeature_5: 'The platform has strong market potential with contracts already signed with major clients. Market research indicates a potential ROI of 300% within 5 years.',
      judging_specialfeature_6: 'The platform promotes environmental sustainability by optimizing resource usage and reducing waste through intelligent management and efficient allocation.',
      entry_photo: null,
      judging_doc_material: null,
      judging_video_material_1: 'https://www.youtube.com/watch?v=smartcity-demo',

      // Declaration
      about_submission_usage: true,
      about_application: true,
      entry_patent: 'Y',
      entry_patent_number: 'HK1234567',
      entry_patent_title: `${selectedEntryTitle.eng} - Patent`,
      entry_patent_proprietor: selectedCompany.eng,
      entry_patent_country: 'Hong Kong',
      entry_patent_date: '2020-03-15',
      entry_patent_status: 'Approved',
      entry_dispute: 'N',
      entry_dispute_description: '',
      event_cost: true,
      data_policy_1: true,
      data_policy_2: true,

      // Captcha (will be validated separately)
      checknum: captchaCode
    });

    // Update conditional field visibility
    setShowPatentSection(true);
    setShowDisputeDescription(false);
    setShowIncubationDescription(true);
  };

  // Get CSS class for field validation
  const getFieldClass = (fieldName: string) => {
    const hasError = validationErrors.some(error => error.field === fieldName);
    const isFirstError = validationErrors.length > 0 && validationErrors[0].field === fieldName;

    if (hasError) {
      return isFirstError ? 'form-field-error first-error' : 'form-field-error';
    }
    return '';
  };

  // Get CSS class for supplementary materials section validation
  const getSupplementaryMaterialsClass = () => {
    const hasError = validationErrors.some(error => error.field === 'supplementary_materials');
    const isFirstError = validationErrors.length > 0 && validationErrors[0].field === 'supplementary_materials';

    if (hasError) {
      return isFirstError ? 'entry_supplementary form-field-error first-error' : 'entry_supplementary form-field-error';
    }
    return 'entry_supplementary';
  };

  //// console.log('About to check isSubmitting:', isSubmitting);
  if (isSubmitting) {
    return <LoadingSpinner message="Submitting your application..." />;
  }

  //// console.log('About to check showSuccess:', showSuccess);
  if (showSuccess) {
    return (
      <SuccessMessage
        onClose={() => setShowSuccess(false)}
      />
    );
  }

  // Check if application deadline has passed
  if (isApplicationClosed()) {
    return (
      <div>
        {/* Header section - same as form */}
        <section className="heading">
          <div className="container">
            <div className="row">
              <div className="main_banner">
                <img className="logo" src="/main_banner.png" alt="Logo" />
                <h1 className="width_remark" style={{ marginTop: 15, marginBottom: 50 }}>
                  2025 智慧生活夥伴大獎<br />Application Form 參賽表格
                </h1>
              </div>
              <div className="logo-container">
                <div className="organiser">
                  <p style={{ fontSize: 14, color: '#004FAD' }}><b>Organiser</b></p>
                  <img className="etnet-logo" src="/etnet.png" width="100%" alt="Organiser" />
                </div>
                <div className="co-organiser">
                  <p style={{ fontSize: 14, color: '#004FAD' }}><b>Co-organiser</b></p>
                  <img className="cyberport-logo" src="/Cyberport_Logo.png" style={{ marginTop: '-6%' }} width="95%" alt="Co-organiser" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Closed message section */}
        <section className="form_content">
          <div className="container">
            <div style={{
              padding: '40px 0',
              textAlign: 'left',
              marginLeft: '0',
              marginRight: 'auto'
            }}>
              <p style={{
                fontSize: '30px',
                fontWeight: 'bold',
                color: '#004FAD',
                margin: '0 0 10px 0',
                lineHeight: '1.4'
              }}>
                此報名已經結束，感謝閣下支持！
              </p>
              <p style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: '#004FAD',
                margin: '0 0 20px 0',
                lineHeight: '1.4'
              }}>
                Closed for applications, thank you for your support!
              </p>

              {/* Return to website button */}
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  backgroundColor: '#004FAD',
                  color: 'white',
                  border: 'none',
                  padding: '12px 30px',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#003d8a'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#004FAD'}
              >
                Return to website 返回網站
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  //// console.log('About to render main form...');
  return (
    <div>
      {/* Test Data Fill Button - Fixed on left side - Only show in LOCAL environment */}
      {(() => {
        const isLocalEnv = (import.meta.env.VITE_ENV === 'LOCAL');
        if (isLocalEnv) {
          // console.log('🧪 Test fill button enabled - LOCAL environment detected');
        }
        return isLocalEnv;
      })() && (
        <button
          type="button"
          onClick={fillTestData}
          className="test-fill-button"
          title="Click to auto-fill form with test data"
        >
          🧪 Fill Test Data
          <br />
          <small style={{ fontSize: '11px', opacity: 0.9 }}>Auto Fill Form</small>
        </button>
      )}

      <section className="heading">
        <div className="container">
          <div className="row">
            <div className="main_banner">
              <img className="logo" src="/main_banner.png" alt="Logo" />
              <h1 className="width_remark" style={{ marginTop: 15, marginBottom: 50 }}>
                2025 智慧生活夥伴大獎<br />Application Form 參賽表格
              </h1>
            </div>
            <div className="logo-container">
              <div className="organiser">
                <p style={{ fontSize: 14, color: '#004FAD' }}><b>Organiser</b></p>
                <img className="etnet-logo" src="/etnet.png" width="100%" alt="Organiser" />
              </div>
              <div className="co-organiser">
                <p style={{ fontSize: 14, color: '#004FAD' }}><b>Co-organiser</b></p>
                <img className="cyberport-logo" src="/Cyberport_Logo.png" style={{ marginTop: '-6%' }} width="95%" alt="Co-organiser" />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="form_content">
        <div className="container">
          <form name="applicationform" id="applicationform" onSubmit={handleSubmit} encType="multipart/form-data">
            <h1 className="width_remark">Instructions 填寫說明</h1>
            <table className="instructions">
              <tbody>
                <tr>
                  <td>
          <div className="instructions">
                      Please fill in all parts of this Form and attach additional information where necessary by email，submission by email to <a href="mailto:smartcityawards@etnet.com.hk">smartcityawards@etnet.com.hk</a> should be delivered on or before 19 September 2025 at 10:00 AM. Late submission will not be accepted<br /><br />
                      請填寫所有部分。<br />
                      如有需要請附加額外資訊，參賽者可於2025年9月19日上午10時或之前電郵此表格到<a href="mailto:smartcityawards@etnet.com.hk">smartcityawards@etnet.com.hk</a>。遲交表格將不被接納。
          </div>
                  </td>
                </tr>
              </tbody>
            </table>

          {/* Part I - Entry Information */}
            <div>
              <h1 className="width_remark">Part I - Entry Information 參賽機構作品摘要</h1>
              <div style={{ clear: 'both', height: 0 }}></div>
              <div className="box">
                <div className="label">Entry Title 參賽機構作品名稱</div>
              </div>
              <div className="box col_2">
                <div className="label">ENG<span style={{ color: 'red' }}>*</span></div>
                <div className="inputWrap">
                  <input
                    type="text"
                    name="entry_title_eng"
                    maxLength={200}
                    value={formData.entry_title_eng}
                    onChange={handleInputChange}
                    className={`entry_title_eng ${getFieldClass('entry_title_eng')}`}
                  />
                  <FormError field="entry_title_eng" errors={validationErrors} />
                </div>
              </div>
              <div className="box col_2">
                <div className="label">中</div>
                <div className="inputWrap">
                  <input
                    type="text"
                    name="entry_title_chi"
                    maxLength={40}
                    value={formData.entry_title_chi}
                    onChange={handleInputChange}
                    className={`entry_title_chi ${getFieldClass('entry_title_chi')}`}
                  />
                  <FormError field="entry_title_chi" errors={validationErrors} />
                </div>
              </div>
              <div className="box">
                <div className="label" id="EntryDescriptionTitle">Entry Description 參賽機構作品簡介<span style={{ color: 'red' }}>*</span></div>
                <div className="inputWrap">
                  <textarea
                    placeholder="Please state no more than 150 words in English and clearly written in paragraphs. 請用英文以不可多於150字清楚填寫大小楷及分行。"
                    name="entry_info"
                    value={formData.entry_info}
                    onChange={handleInputChange}
                    className={`entry_info ${getFieldClass('entry_info')}`}
                  ></textarea>
                  <FormError field="entry_info" errors={validationErrors} value={formData.entry_info} maxWords={150} />
                </div>
              </div>
            </div>

            {/* Part II - Company Information */}
            <div>
              <h1 className="width_remark">Part II - Company Information 參賽機構簡介</h1>
              <span className="remark" style={{ marginBottom: 0 }}>*Note 備註:  The below company information will be featured in the award promotions (if applicable). 以下機構簡介將刊登於活動推廣（如適用）。</span>
            <div className="box">
                <div className="label" id="CompanyNameTitle" style={{ marginTop: 20 }}>Company Name 機構名稱</div>
            </div>
              <div className="box col_2" id="CompanyNameEng">
                <div className="label">ENG<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                  <input type="text" name="company_name_eng" maxLength={200} value={formData.company_name_eng} onChange={handleInputChange} className={`company_name_eng ${getFieldClass('company_name_eng')}`} />
                  <FormError field="company_name_eng" errors={validationErrors} />
              </div>
            </div>
              <div className="box col_2" id="CompanyNameChi">
              <div className="label">中</div>
              <div className="inputWrap">
                  <input type="text" name="company_name_chi" maxLength={100} value={formData.company_name_chi} onChange={handleInputChange} className={`company_name_chi ${getFieldClass('company_name_chi')}`} />
                  <FormError field="company_name_chi" errors={validationErrors} />
              </div>
            </div>
              <div className="box">
                <div className="label" id="CompanyDescriptionTitle">Company Description 機構簡介<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                  <textarea placeholder="Please state no more than 150 words in English and clearly written in paragraphs. 請用英文以不多於150字清楚填寫大小楷及分行。" name="company_info" value={formData.company_info} onChange={handleInputChange} className={`company_info ${getFieldClass('company_info')}`}></textarea>
                  <FormError field="company_info" errors={validationErrors} value={formData.company_info} maxWords={150} />
              </div>
                <br /><span className="remark" id="CompanyDescriptionRemark" style={{ marginLeft: '30%' }}>*Company profile will be featured in the award booklet (in English) without further editing.<br /><span style={{ marginLeft: '15%' }}>公司簡介將直接刊登於活動場刊 （電子版）（英文）。</span></span>
              </div>
              <div className="box col_2">
                <div className="label" style={{ width: '100%', marginTop: 8 }}>Company Address 公司地址<span style={{ color: 'red' }}>*</span></div><br />
                <div className="inputWrap">
                  <textarea name="company_address" rows={4} cols={70} value={formData.company_address} onChange={handleInputChange} className={`company_address ${getFieldClass('company_address')}`}></textarea>
                  <FormError field="company_address" errors={validationErrors} />
              </div>
            </div>
              <div className="box col_2" style={{ verticalAlign: 'top' }}>
                <table>
                  <tbody>
                    <tr>
                      <td><div className="label" style={{ width: '100%', verticalAlign: 'top' }}>Name of Chief Executive Officer 行政總裁姓名</div></td>
                      <td></td>
                      <td><div className="label" style={{ width: '100%', verticalAlign: 'top' }}>Nature of Business 業務性質<span style={{ color: 'red' }}>*</span></div></td>
                    </tr>
                    <tr>
                      <td><input type="text" name="company_ceo_name" maxLength={100} value={formData.company_ceo_name} onChange={handleInputChange} className={`company_ceo_name ${getFieldClass('company_ceo_name')}`} /></td>
                      <td></td>
                      <td><input type="text" name="company_business" maxLength={100} value={formData.company_business} onChange={handleInputChange} className={`company_business ${getFieldClass('company_business')}`} /></td>
                    </tr>
                    <tr>
                      <td><div className="label" style={{ width: '100%', verticalAlign: 'top', marginTop: 18 }}>URL 機構網址<span style={{ color: 'red' }}>*</span></div></td>
                    </tr>
                    <tr>
                      <td><input type="text" name="company_url" maxLength={100} value={formData.company_url} onChange={handleInputChange} className={`company_url ${getFieldClass('company_url')}`} /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div id="CompanyInfomation">
                <div className="box col_2">
                  <div className="label" style={{ width: '100%' }}>Business Registration Certificate No. 商業登記證號碼： <span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                    <input type="text" name="company_registration_no" maxLength={20} value={formData.company_registration_no} onChange={handleInputChange} className={`company_registration_no ${getFieldClass('company_registration_no')}`} />
                    <FormError field="company_registration_no" errors={validationErrors} />
              </div>
            </div>
            <div className="box col_2">
                  <div className="label" style={{ width: '100%' }}>Certificate of Incorporation No. 機構註冊證書號碼：<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                    <input type="text" name="company_incorporation_no" maxLength={20} value={formData.company_incorporation_no} onChange={handleInputChange} className={`company_incorporation_no ${getFieldClass('company_incorporation_no')}`} />
                    <FormError field="company_incorporation_no" errors={validationErrors} />
              </div>
            </div>
            <div className="box col_2">
                  <div className="label" style={{ width: '100%' }}>Date of Incorporation 機構成立日期：</div><br />
              <div className="inputWrap">
                    <input type="date" placeholder="dd/mm/yyyy" min="1902-01-01" name="company_incorporation_date" maxLength={10} value={formData.company_incorporation_date} onChange={handleInputChange} className={`company_incorporation_date ${getFieldClass('company_incorporation_date')}`} lang="en" />
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>Format: dd/mm/yyyy</div>
                    <FormError field="company_incorporation_date" errors={validationErrors} />
              </div>
            </div>
                <div className="box col_2 startup">
                  <div className="label" style={{ width: '100%' }}>Please mark if you are a Start-up Company 機構是否初創企業<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                    <ul>
                      <li className="radio"><input type="radio" id="startup_Y" name="startup" value="Y" checked={formData.startup === 'Y'} onChange={handleInputChange} /><label htmlFor="startup_Y">Yes 是</label></li>
                      <li className="radio"><input type="radio" id="startup_N" name="startup" value="N" checked={formData.startup === 'N'} onChange={handleInputChange} /><label htmlFor="startup_N">No 否</label></li>
                    </ul>
                    <FormError field="startup" errors={validationErrors} />
              </div>
            </div>
              </div>
              <div className="box col_2 no_of_employee">
                <div className="label" style={{ width: '100%' }}>No. of Employee 員工數目</div>
                <div className="inputWrap">
                  <div className="radio"><input type="radio" id="no_of_employee_50_or_less" name="company_employee_no" value="50_or_less" checked={formData.company_employee_no === '50_or_less'} onChange={handleInputChange} /><label htmlFor="no_of_employee_50_or_less">≤ 50</label></div>
                  <div className="radio"><input type="radio" id="no_of_employee_51_to_100" name="company_employee_no" value="51_to_100" checked={formData.company_employee_no === '51_to_100'} onChange={handleInputChange} /><label htmlFor="no_of_employee_51_to_100">51 - 100</label></div>
                  <div className="radio"><input type="radio" id="no_of_employee_101_to_500" name="company_employee_no" value="101_to_500" checked={formData.company_employee_no === '101_to_500'} onChange={handleInputChange} /><label htmlFor="no_of_employee_101_to_500">101 - 500</label></div>
                  <div className="radio"><input type="radio" id="no_of_employee_501_to_1000" name="company_employee_no" value="501_to_1000" checked={formData.company_employee_no === '501_to_1000'} onChange={handleInputChange} /><label htmlFor="no_of_employee_501_to_1000">501 - 1000</label></div>
                  <div className="radio"><input type="radio" id="no_of_employee_over_1001" name="company_employee_no" value="over_1001" checked={formData.company_employee_no === 'over_1001'} onChange={handleInputChange} /><label htmlFor="no_of_employee_over_1001">≥ 1001</label></div>
                  <FormError field="company_employee_no" errors={validationErrors} />
                </div>
              </div>
              <div className="box col_2 entryIncubation" style={{ verticalAlign: 'top' }}>
                <div className="label" style={{ width: '100%' }}>Incubation Programme 培育計劃成員<span style={{ color: 'red' }}>*</span></div>
                <div className="inputWrap">
                  <div className="radio"><input type="radio" id="entry_incubation_N" name="entry_incubation" value="N" checked={formData.entry_incubation === 'N'} onChange={handleInputChange} /><label htmlFor="entry_incubation_N">Not Applicable 不適用</label></div>
                  <div className="radio"><input type="radio" id="entry_incubation_cyberport" name="entry_incubation" value="C" checked={formData.entry_incubation === 'C'} onChange={handleInputChange} /><label htmlFor="entry_incubation_cyberport">Cyberport 數碼港</label></div>
                  <div className="radio"><input type="radio" id="entry_incubation_hkstp" name="entry_incubation" value="H" checked={formData.entry_incubation === 'H'} onChange={handleInputChange} /><label htmlFor="entry_incubation_hkstp">Hong Kong Science & Technology Parks Corporation (HKSTP) 香港科技園公司</label></div>
                  <div className="radio"><input type="radio" id="entry_incubation_other" name="entry_incubation" value="O" checked={formData.entry_incubation === 'O'} onChange={handleInputChange} /><label htmlFor="entry_incubation_other">Others 其他</label></div>
                  <FormError field="entry_incubation" errors={validationErrors} />
                  <div className="box incubationDescription" style={{ display: showIncubationDescription ? 'block' : 'none' }}>
                    <div className="label" style={{ width: '100%' }}>Please specify 請明確說明:<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                <textarea
                        name="entry_incubation_description"
                  rows={4}
                        cols={70}
                        value={formData.entry_incubation_description}
                        onChange={handleInputChange}
                        className={`entry_incubation_description ${getFieldClass('entry_incubation_description')}`}
                      ></textarea>
                      <FormError field="entry_incubation_description" errors={validationErrors} />
              </div>
                  </div>
                </div>
              </div>
              <div className="box">
                <div className="label">Logo 商標</div>
                <span className="remark">Please provide logo in below formats and logo guideline. 請提供以下商標格式及商標使用指引：<br /><span style={{ color: 'red' }}>*</span>Logo will apply for all marketing materials. Once submitted, no amendments can be made. 備註：商標將刊登於宣傳及推廣資料上,一旦提交將不得更改。</span>
                <div className="inputWrap">
                  <div className="filesWrap">1. AI or EPS (color in CMYK)<span style={{ color: 'red' }}>*</span></div>
                  <input type="file" accept="application/postscript" name="company_logo_1" className="company_logo_1" onChange={handleFileChange} />
                  <FormError field="company_logo_1" errors={validationErrors} />
                </div>
                <br /><br />
                <div className="inputWrap">
                  <div className="filesWrap">2. JPEG (color in CMYK)<span style={{ color: 'red' }}>*</span></div>
                  <input type="file" accept="image/jpeg" name="company_logo_2" className="company_logo_2" onChange={handleFileChange} />
                  <FormError field="company_logo_2" errors={validationErrors} />
                </div>
                <br /><br />
                <div className="inputWrap">
                  <div className="filesWrap">3. Corporate logo and brand guideline in PowerPoint / PDF format (max. file size：5 MB)<span style={{ color: 'red' }}>*</span></div>
                  <input type="file" accept="application/vnd.ms-powerpoint,application/pdf" name="corporate_guideline" className="corporate_guideline" onChange={handleFileChange} />
                  <FormError field="corporate_guideline" errors={validationErrors} />
                </div>
                <span className="remark">請以簡報 / PDF提供商標使用指引。(檔案容量不多於5 MB)</span>
            </div>
          </div>

            {/* Part III - Contact Details */}
            <div>
              <h1 className="width_remark">Part III - Contact Details 聯絡人資料</h1><br />
            <div>Primary Contact Person 第一聯絡人</div>
            <div className="box col_2">
                <div className="label">Name 姓名<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                  <input type="text" name="contact_name_1" maxLength={100} value={formData.contact_name_1} onChange={handleInputChange} className={`contact_name ${getFieldClass('contact_name_1')}`} />
                  <FormError field="contact_name_1" errors={validationErrors} />
              </div>
            </div>
            <div className="box col_2">
                <div className="label">Title 職位<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                  <input type="text" name="contact_title_1" maxLength={100} value={formData.contact_title_1} onChange={handleInputChange} className={`contact_title ${getFieldClass('contact_title_1')}`} />
                  <FormError field="contact_title_1" errors={validationErrors} />
              </div>
            </div>
            <div className="box col_2">
                <div className="label">Phone 電話<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                  <input type="text" name="contact_phone_1" maxLength={11} value={formData.contact_phone_1} onChange={handleInputChange} className={`contact_phone ${getFieldClass('contact_phone_1')}`} />
                  <FormError field="contact_phone_1" errors={validationErrors} />
              </div>
            </div>
            <div className="box col_2">
                <div className="label">Email 電郵<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                  <input type="text" name="contact_email_1" maxLength={50} value={formData.contact_email_1} onChange={handleInputChange} className={`contact_email ${getFieldClass('contact_email_1')}`} />
                  <FormError field="contact_email_1" errors={validationErrors} />
              </div>
            </div>
            <div>Secondary Contact Person 第二聯絡人</div>
            <div className="box col_2">
                <div className="label">Name 姓名<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                  <input type="text" name="contact_name_2" maxLength={100} value={formData.contact_name_2} onChange={handleInputChange} className={`contact_name ${getFieldClass('contact_name_2')}`} />
                  <FormError field="contact_name_2" errors={validationErrors} />
              </div>
            </div>
            <div className="box col_2">
                <div className="label">Title 職位<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                  <input type="text" name="contact_title_2" maxLength={100} value={formData.contact_title_2} onChange={handleInputChange} className={`contact_title ${getFieldClass('contact_title_2')}`} />
                  <FormError field="contact_title_2" errors={validationErrors} />
              </div>
            </div>
            <div className="box col_2">
                <div className="label">Phone 電話<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                  <input type="text" name="contact_phone_2" maxLength={11} value={formData.contact_phone_2} onChange={handleInputChange} className={`contact_phone ${getFieldClass('contact_phone_2')}`} />
                  <FormError field="contact_phone_2" errors={validationErrors} />
              </div>
            </div>
            <div className="box col_2">
                <div className="label">Email 電郵<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                  <input type="text" name="contact_email_2" maxLength={50} value={formData.contact_email_2} onChange={handleInputChange} className={`contact_email ${getFieldClass('contact_email_2')}`} />
                  <FormError field="contact_email_2" errors={validationErrors} />
              </div>
            </div>
          </div>
            {/* Part IV - Award Categories */}
            <div>
              <h1 className="width_remark">Part IV - Award Categories 獎項類別<span style={{ color: 'red' }}>*</span></h1>
              <span className="remark" data-group="corporate">*Note 備註:<br />ONE entry is only allowed to enter ONE award stream. 參賽作品只可參加一個獎項類別。<br />The applicant is advised to submit its application(s) to the most appropriate category. The Organiser reserves the final rights to reallocate a submission to the most appropriate category if needed.<br />參賽機構所遞交的參賽作品應呈交至最適當的獎項類別，主辦機構保留最終權利更改參賽作品至更適合之獎項類別。</span>
            <div className="box awardCategory">
                <div className="label" data-group="corporate" style={{ display: 'none' }}>Please mark "X" to the award stream that is to be applied. 請以"X" 標示參賽的獎項類別以供參考: <span style={{ color: 'red' }}>*</span></div>
                <div className="inputWrap" data-group="corporate">
                <ul>
                  {AWARD_CATEGORIES.map((category) => (
                    <li key={category.value} className="radio col_3">
                      <input
                        type="radio"
                          style={{ width: '100%' }}
                        id={`award_category_${category.value}`}
                          name="award_category"
                        value={category.value}
                          checked={formData.award_category === category.value}
                          onChange={handleInputChange}
                      />
                      <label htmlFor={`award_category_${category.value}`}>
                          {category.label}<br />{category.labelChi}
                      </label>
                    </li>
                  ))}
                </ul>
                  <FormError field="award_category" errors={validationErrors} />
              </div>
            </div>
          </div>
            {/* Part V - Judging Materials */}
            <div>
              <h1 className="width_remark">Part V - Judging Materials 評審資料</h1>
              <span className="remark">Please state special features of the application with respect to the following judging criteria where appropriate.<br />請就以下評審準則簡述參賽作品的特性。（如有需要請自行另加頁填寫）:<br /></span>
            <div className="box">
                <div className="label">Judging is based on the following 6 factors (Please use around 100 words to elaborate each factor), please fill in "Not Applicable" for inapplicable items.<br />大會評審基於以下六個準則（請為每個準則以大約100字作闡述），如有不適用者，請填上「不適用」。</div>
            </div>
            <div className="box">
                <div className="label">Innovation and Creativity 創新和創意 (15%)<span style={{ color: 'red' }}>*</span></div>
                <div className="inputWrap">
                  <textarea name="judging_specialfeature_1" value={formData.judging_specialfeature_1} onChange={handleInputChange} className={`judging_specialfeature_1 ${getFieldClass('judging_specialfeature_1')}`}></textarea>
                  <FormError field="judging_specialfeature_1" errors={validationErrors} />
            </div>
            </div>
            <div className="box">
                <div className="label">Functionality and Ergonomics 功能和人體工學 (15%)<span style={{ color: 'red' }}>*</span></div>
                <div className="inputWrap">
                  <textarea name="judging_specialfeature_2" value={formData.judging_specialfeature_2} onChange={handleInputChange} className={`judging_specialfeature_2 ${getFieldClass('judging_specialfeature_2')}`}></textarea>
                  <FormError field="judging_specialfeature_2" errors={validationErrors} />
                </div>
            </div>
            <div className="box">
                <div className="label">Quality 品質 (15%)<span style={{ color: 'red' }}>*</span></div>
                <div className="inputWrap">
                  <textarea name="judging_specialfeature_3" value={formData.judging_specialfeature_3} onChange={handleInputChange} className={`judging_specialfeature_3 ${getFieldClass('judging_specialfeature_3')}`}></textarea>
                  <FormError field="judging_specialfeature_3" errors={validationErrors} />
                </div>
            </div>
            <div className="box">
                <div className="label">Benefits and Impact 裨益和影響 (20%)<span style={{ color: 'red' }}>*</span></div>
              <div className="inputWrap">
                  <textarea name="judging_specialfeature_4" value={formData.judging_specialfeature_4} onChange={handleInputChange} className={`judging_specialfeature_4 ${getFieldClass('judging_specialfeature_4')}`}></textarea>
                  <FormError field="judging_specialfeature_4" errors={validationErrors} />
                </div>
              </div>
              <div className="box">
                <div className="label">Market Potential / Performance 市場潛力 / 表現 (20%)<span style={{ color: 'red' }}>*</span></div>
                <div className="inputWrap">
                  <textarea name="judging_specialfeature_5" value={formData.judging_specialfeature_5} onChange={handleInputChange} className={`judging_specialfeature_5 ${getFieldClass('judging_specialfeature_5')}`}></textarea>
                  <FormError field="judging_specialfeature_5" errors={validationErrors} />
            </div>
          </div>
              <div className="box">
                <div className="label">Ecological Compatibility 生態兼容性  (15%)<span style={{ color: 'red' }}>*</span><br />*Potential Environmental and/or Ecological Impact 對環境 及/或 生態的潛在影響</div>
                <div className="inputWrap">
                  <textarea name="judging_specialfeature_6" value={formData.judging_specialfeature_6} onChange={handleInputChange} className={`judging_specialfeature_6 ${getFieldClass('judging_specialfeature_6')}`}></textarea>
                  <FormError field="judging_specialfeature_6" errors={validationErrors} />
                  </div>
                </div>
              {/* Entry Supplementary */}
              <div className={getSupplementaryMaterialsClass()}>
                <div className="label"><u>Supplementary of Entry for Judging Panel Reference</u> <u>附加資料以作大會評審團參考</u></div><br />
                <div className="label">Please submit below at least 1 document as reference. 請提交以下最少一項的文件作參考<span style={{ color: 'red' }}>*</span></div>
                <FormError field="supplementary_materials" errors={validationErrors} />
                <br />
                <div className="inputWrap">
                  <input type="checkbox" id="entry_photo_c" name="judging_video_material[]" value="Y" />
                  <label htmlFor="entry_photo_c">Entry diagram or photo : 參賽作品圖表或相片</label><br />
                  <div id="entry_photo" style={{ paddingLeft: 25 }} data-group="entry_photo">
                    <div className="box col_2">
                      <span>One page diagram or photo (max. file size: 3 MB)</span><span style={{ color: 'red' }}>*</span><br />
                      <span>請提供參賽作品圖表或相片簡介。(檔案容量不多於3 MB)</span><span style={{ color: 'red' }}>*</span><br />
                      <input type="file" accept="image/jpeg, .png" name="entry_photo" className="entry_photo" onChange={handleFileChange} />
                      <FormError field="entry_photo" errors={validationErrors} />
                    </div><br />
              </div>
                  <input type="checkbox" id="judging_doc_material_c" name="judging_video_material[]" value="Y" />
                  <label htmlFor="judging_doc_material_c">Document in PowerPoint / PDF format : 簡報 / PDF 資料</label><br />
                  <div id="judging_doc_material" style={{ paddingLeft: 25 }} data-group="judging_doc_material">
                <div className="inputWrap">
                      <span>Supporting document should not more than 5 pages in PowerPoint / PDF format. (max. file size：5 MB)<span style={{ color: 'red' }}>*</span><br />請提供不多於五頁簡報 / PDF 資料以作評審團參考。(檔案容量不多於5 MB)</span><span style={{ color: 'red' }}>*</span><br />
                      <input type="file" accept="application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.slideshow,application/vnd.openxmlformats-officedocument.presentationml.presentation" name="judging_doc_material" onChange={handleFileChange} />
                      <FormError field="judging_doc_material" errors={validationErrors} />
                    </div><br />
                  </div>
                  <div className="box video">
                    <input type="checkbox" id="judging_video_material_c" name="judging_video_material[]" value="Y" />
                    <label htmlFor="judging_video_material_c">Video Link : 影片鏈結</label><br />
                    <div id="video_material" data-group="judging_video_material">
                      <div className="inputWrap">
                        <input type="text" name="judging_video_material_1" maxLength={200} value={formData.judging_video_material_1} onChange={handleInputChange} className={`judging_video_material_1 ${getFieldClass('judging_video_material_1')}`} />
                        <FormError field="judging_video_material_1" errors={validationErrors} />
                </div>
              </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Part VI - Declaration */}
            <div>
              <h1 className="width_remark">Part VI - Declaration 聲明</h1><br />
              <div id="declaration">
                <div className="box about_the_submission_usage">
                  <div className="label">1. About The Submission Usage關於資料用途<span style={{ color: 'red' }}>*</span></div>
                <div className="inputWrap">
                    <div className="checkbox" style={{ paddingTop: 7 }}><input type="checkbox" id="about_submission_usage" name="about_submission_usage" value="Y" checked={formData.about_submission_usage} onChange={handleInputChange} /><label htmlFor="about_submission_usage">I / We confirm that the details are accurate and applicable in all promotion materials related to the Awards including website, booklet, trophy and print advertisement, etc. If there is any changes need to be made based on the form, I/we have only one chance to change without additional cost. If there is any additional amendment has to be made further to the signed submission usage form, HK$800 will be imposed each time. (For details, please refer to the Terms & Conditions point #6)<br />本人/我們確認所有與獎項相關的宣傳材料包括網站、小冊子、獎座和印刷廣告等的詳細資料準確和適用。如果需要對已提交資料進行任何更改，本人/我們只接受一次無需額外費用的修改，其後將收取每次港幣 800 元的修改費用。(詳情請參閲推廣活動條款及細則之第六項條款及細則)</label></div>
                    <FormError field="about_submission_usage" errors={validationErrors} />
                  </div>
                  </div>
                <div className="box about_the_application">
                  <div className="label">2. About The Application 關於參賽機構<span style={{ color: 'red' }}>*</span></div>
                  <div className="inputWrap">
                    <div className="checkbox" style={{ paddingTop: 7 }}><input type="checkbox" id="about_application" name="about_application" value="Y" checked={formData.about_application} onChange={handleInputChange} /><label htmlFor="about_application">I / We declare that the application is submitted by locally registered entities or residents in Hong Kong.<br />本人/我們聲明參賽機構為香港註冊公司、機構或香港居民。</label></div>
                    <FormError field="about_application" errors={validationErrors} />
                </div>
              </div>
                <div className="box entryPatent">
                  <div className="label">3. Please choose either one：請選擇其中一項：<span style={{ color: 'red' }}>*</span></div>
                  <div className="inputWrap">
                    <div className="radio"><input type="radio" id="entry_patent_N" name="entry_patent" value="N" checked={formData.entry_patent === 'N'} onChange={handleInputChange} /><label htmlFor="entry_patent_N">The submitting entry has not been applied for any patent. 此參賽項目沒有申請任何專利。</label></div>
                    <div className="radio"><input type="radio" id="entry_patent_Y" name="entry_patent" value="Y" checked={formData.entry_patent === 'Y'} onChange={handleInputChange} /><label htmlFor="entry_patent_Y">Declaration on patent related information for the submitting entry：此參賽項目之專利資料列明如下：</label></div>
                    <FormError field="entry_patent" errors={validationErrors} />
                  </div>
                </div>
                <div className="box patentSection" style={{ display: showPatentSection ? 'block' : 'none' }}>
                  <div className="box box_4_6">
                    <div className="label">Patent Number or Application Number (if pending)<br />專利編號或申請編號（如申請中）<span style={{ color: 'red' }}>*</span></div>
                    <div className="inputWrap">
                      <input
                        type="text"
                        name="entry_patent_number"
                        maxLength={50}
                        value={formData.entry_patent_number}
                        onChange={handleInputChange}
                        className={`entry_patent_number ${getFieldClass('entry_patent_number')}`}
                      />
                      <FormError field="entry_patent_number" errors={validationErrors} />
                    </div>
                  </div>
                  <div className="box box_4_6">
                    <div className="label">Patent Grant Title or Title<br />批予專利名稱或發明名稱<span style={{ color: 'red' }}>*</span></div>
                    <div className="inputWrap">
                      <input
                        type="text"
                        name="entry_patent_title"
                        maxLength={100}
                        value={formData.entry_patent_title}
                        onChange={handleInputChange}
                        className={`entry_patent_title ${getFieldClass('entry_patent_title')}`}
                      />
                      <FormError field="entry_patent_title" errors={validationErrors} />
                    </div>
                  </div>
                  <div className="box box_4_6">
                    <div className="label">Proprietor or Inventor<br />專利所有人或發明人<span style={{ color: 'red' }}>*</span></div>
                    <div className="inputWrap">
                      <input
                        type="text"
                        name="entry_patent_proprietor"
                        maxLength={100}
                        value={formData.entry_patent_proprietor}
                        onChange={handleInputChange}
                        className={`entry_patent_proprietor ${getFieldClass('entry_patent_proprietor')}`}
                      />
                      <FormError field="entry_patent_proprietor" errors={validationErrors} />
                    </div>
                  </div>
                  <div className="box box_4_6">
                    <div className="label">Country<br />國家<span style={{ color: 'red' }}>*</span></div>
                    <div className="inputWrap">
                      <input
                        type="text"
                        name="entry_patent_country"
                        maxLength={50}
                        value={formData.entry_patent_country}
                        onChange={handleInputChange}
                        className={`entry_patent_country ${getFieldClass('entry_patent_country')}`}
                      />
                      <FormError field="entry_patent_country" errors={validationErrors} />
                    </div>
                  </div>
                  <div className="box box_4_6">
                    <div className="label">Filing Date<br />提交日期<span style={{ color: 'red' }}>*</span></div>
                    <div className="inputWrap">
                      <input
                        type="date"
                        placeholder="dd/mm/yyyy"
                        min="1950-01-01"
                        name="entry_patent_date"
                        maxLength={10}
                        value={formData.entry_patent_date}
                        onChange={handleInputChange}
                        className={`entry_patent_date ${getFieldClass('entry_patent_date')}`}
                        lang="en"
                      />
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>Format: dd/mm/yyyy</div>
                      <FormError field="entry_patent_date" errors={validationErrors} />
                    </div>
                  </div>
                  <div className="box box_4_6">
                    <div className="label">Status<br />狀況<span style={{ color: 'red' }}>*</span></div>
                    <div className="inputWrap">
                      <div className="radio">
                        <input
                          type="radio"
                          id="entry_patent_status_approved"
                          name="entry_patent_status"
                          value="Approved"
                          checked={formData.entry_patent_status === 'Approved'}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="entry_patent_status_approved">Approved 已批准</label>
                      </div>
                      <div className="radio">
                        <input
                          type="radio"
                          id="entry_patent_status_pending"
                          name="entry_patent_status"
                          value="Pending"
                          checked={formData.entry_patent_status === 'Pending'}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="entry_patent_status_pending">Pending 處理中</label>
                      </div>
                      <FormError field="entry_patent_status" errors={validationErrors} />
                    </div>
                  </div>
                </div>
                <div className="box entryDispute" style={{ paddingBottom: 0 }}>
                  <div className="label">4. Please choose either one：請選擇其中一項：<span style={{ color: 'red' }}>*</span></div>
                <div className="inputWrap">
                    <div className="radio"><input type="radio" id="entry_dispute_N" name="entry_dispute" value="N" checked={formData.entry_dispute === 'N'} onChange={handleInputChange} /><label htmlFor="entry_dispute_N">I / We declare that there is no dispute in any place over the world over intellectual property right about the submitting entry. <br />本人/我們在此聲明此參賽項目在世界任何地方沒有任何知識產權爭議。</label></div>
                    <div className="radio"><input type="radio" id="entry_dispute_Y" name="entry_dispute" value="Y" checked={formData.entry_dispute === 'Y'} onChange={handleInputChange} /><label htmlFor="entry_dispute_Y">I / We declare that there are / were below dispute(s) over intellectual property right about the submitting entry and I / We have obligation to disclose the corresponding information for the Judging Panel to consider the eligibility of the submitting entry. <br />本人/我們在此聲明此參賽項目有或曾經有以下知識產權爭議，本人/我們須披露有關資料予大會評審團考慮此參賽項目的參賽資格。</label></div><br />
                    <FormError field="entry_dispute" errors={validationErrors} />
                  </div>
                  <div className="box disputeDescription" style={{ display: showDisputeDescription ? 'block' : 'none' }}>
                    <div className="label">Details of intellectual property right dispute(s) 有關知識產權爭議的詳情:<span style={{ color: 'red' }}>*</span></div>
                  <div className="inputWrap">
                    <textarea
                        name="entry_dispute_description"
                        value={formData.entry_dispute_description}
                        onChange={handleInputChange}
                        className={`entry_dispute_description ${getFieldClass('entry_dispute_description')}`}
                      ></textarea>
                      <FormError field="entry_dispute_description" errors={validationErrors} />
                  </div>
                </div>
              </div>
                <div className="box eventCost" style={{ paddingTop: 0 }}>
                  <div className="label">5. Event Administration Cost 活動行政費<span style={{ color: 'red' }}>*</span></div>
                <div className="inputWrap">
                    <div className="checkbox" style={{ paddingTop: 7 }}><input type="checkbox" id="event_cost" name="event_cost" value="Y" checked={formData.event_cost} onChange={handleInputChange} /><label htmlFor="event_cost">I/We declare that we accept the event administration cost, which is HKD$12,000, if awarded under the "Smart Living Partnership Awards  2025".<br />(For details, please refer to the Terms & Conditions point #7). <br />本人/我們在此聲明本人/我們明白如成功於「2025智慧生活夥伴大獎」中獲獎將需支付活動行政費為港幣$12,000元正(詳情請參閲活動條款及細則之第七項條款及細則)。</label></div>
                    <FormError field="event_cost" errors={validationErrors} />
                  </div>
                </div>
              </div>
            </div>
          {/* Terms & Conditions */}
            <div className="botWrap">
              <p>Terms & Conditions 活動條款及細則</p>
            <div className="textWrap">
              <ul>
                  <li>1. Applicants should fill in all the required information about their company and entries, and should cover all the judging criteria. 參賽機構能提供充足資料予主辦機構及大會評審團以作評選之功用。</li>
                  <li>2. The documents and materials supplied are not returnable and will be destroyed by the Organiser after the event. 參賽機構遞交之所有文件及資料將不獲發還，主辦機構會將這些參賽資料於比賽完畢後銷毀。</li>
                  <li>3. The Organiser reserves the right to cancel any part of the Awards due to any unforeseeable circumstance. 如因未能預計的原因，主辦機構保留取消是次比賽全部或部份項目的權利。</li>
                  <li>4. The Organiser reserves the right to accept or reject any entries and have the authority to amend any judging criteria. 主辦機構保留接受或拒絕任何參賽申請的權利。如有需要，主辦機構有權修改大會評審標準。</li>
                  <li>5. Each entrant agrees to hold the Organiser harmless of any claims whatever, including but not limited to copyrights or other intellectual property claims that may be made against any of the Organiser by reason of any such presentation or other use of the entries of the entrant or any part thereof. 參賽機構同意保證主辦機構不須為任何形式的索償負責。此保證包括但不局限於因展出使用其全部或部份作品而引起的版權索償。</li>
                  <li>6. An additional cost (HKD$800) would be charged for amendment on the confirmed submission details each time from the second time onwards. The charge of amendment would be decided by the Organiser by circumstances.由第二次起，每次修改已提交的資料，將收取額外費用(港幣$800元正)。收取費用與否將由主辦機構視情況決定。</li>
                  <li>7.An Event Administration Cost (HKD$12,000) would be charged from each “Smart Living Partnership Awards 2025” award-winning entry. In any circumstances, Organiser will not accept any exits/quits after the result has been announced. The cost will be required to be charged officially once the result announcement is made.於「2025智慧生活夥伴大獎」中獲獎的每個參賽項目會被收取活動行政費(港幣$12,000元正)，於任何情況下，參賽機構或團體如在比賽結果公佈當天或之後提出退出，主辦機構將不接受其活動行政費退出比賽之安排，其費用會於得獎名單公佈時必須支付。</li>
                  <li>8. The Event Administration Cost would include all exposures under the overall event promotion prepared by the Organiser, and the production cost of all relevant event publications and award promotions. 活動行政費將會包括所有主辦機構的整體活動宣傳、製作活動相關的刊物及獎項之行政費用。</li>
                  <li>9. The Organiser shall not be liable in any way for any loss or damage incurred by any entrant for any cancellation of any part of the competition. 主辦機構毋須為參賽機構的損失或損毀負上任何責任。</li>
                  <li>10. In case of any dispute, the decision of the Organiser and the Judging Panel will be final and binding on all parties concerned. 主辦機構及大會評審團保留一切最終決定權。如有任何爭議，一概以主辦機構及大會評審團之最終決定為準。</li>
                  <li>11.	All the above has been translated into English. If there is any inconsistency or ambiguity between the English version and the Chinese version, the Chinese version shall prevail. 如本中文本與英文本若有任何出入之處，一切條款概以中文本為準。</li>
              </ul>
            </div>
          </div>
          {/* Personal Information Collection Statement */}
            <div className="botWrap">
            <p>Personal Information Collection Statement 個人資料收集聲明</p>
            <div className="textWrap">
                <p>ET Net Limited ("The Organiser") will use the personal data provided by you in the application form for the purposes of processing your company’s application to the "Smart Living Partnership Awards 2025" and its directly related purposes, including on-site inspection, review, award selection, award ceremony and publicity events. Your supply of the Personal Data to the Organiser is on a voluntary basis. However, the Organiser may not be able to processing your company’s application to this award program unless you provide us with the Personal Data required in the application form. The Organiser will keep the relevant personal data in strict confidence, but the Organiser may transfer such personal data to third parties if necessary for the above purposes, e.g. when the Organiser feels there is a need to reveal such information to facilitate the program’s on-site inspection, review, award selection, award ceremony and publicity events. You have the right to request access to, and the correction of, your personal data in accordance with the provisions of the Personal Data (Privacy) Ordinance (Cap. 486). Requests for access and correction should be made by letter and sent to the following address:
                <br />Attn: 	      Event Team
                <br />Address:  ET Net Limited, Unit 1505-9 Kodak House II, 321 Java Road, North Point,Hong Kong.</p>
                <p>經濟通 ("主辦機構")使用參賽者/ 團體 / 機構的個人資料主要用於處理「 2025智慧生活夥伴大獎」，以及任何直接有關的用途上。主辦機構也可能使用所提供的個人資料與參賽者/ 團體 / 機構進行活動期間的有關聯絡。主辦機構亦可能將入選決賽的參賽者/ 團體 / 機構的姓名、照片、比賽，以及活動過程刊登在網頁、報章、社交網站、Facebook專頁及其他媒體作為宣傳之用。參賽者/ 團體 / 機構可基於自願性質向主辦機構提供個人資料，但所提供的個人資料能夠使主辦機構為參賽者/ 團體 / 機構參加「 2025智慧生活夥伴大獎」的登記申請。如參賽者/ 團體 / 機構未能向主辦機構提供所需的個人資料，主辦機構可能無法處理參賽者的申請。</p>
                <strong><b>個人資料的轉移</b></strong>
                <p>為滿足上述用途之需要，主辦機構可能會將參賽者 / 團體 / 機構的個人資料轉移給集團內僱員、關連公司 (包括附屬和聯營公司)、由主辦機構就上述用途需要而聘用的印刷服務和獎座供應商、傳媒，以及參賽者 / 團體 / 機構。除此以外，主辦機構不會將參賽者 / 團體 / 機構的個人資料轉移給第三者。</p>
              <p>如有需要，主辦機構或會將參賽者 / 團體 / 機構的姓名、照片、比賽，以及活動過程刊登在網頁、報章、Facebook專頁及其他媒體作為宣傳之用。如法例規定，主辦機構會披露有關資料，也會應執法機關的要求披露上述資料。</p>
                <p>根據《個人資料(私隱)條例》(第486章)，參賽者/ 團體 / 機構有權向主辦機構查詢是否持有其個人資料，並有權取得該等資料的副本及更改任何不準確的資料。索取或更改資料，請以書面方式致函下列地址 :
                <br />
                <br />致: 	活動部
                <br />地址: 經濟通有限公司 香港北角渣華道三二一號柯達大廈二座一五零五至九室。</p>
            </div>
              <div className="box datapolicy">
                <input type="checkbox" id="data_policy_1" name="data_policy_1" value="Y" checked={formData.data_policy_1} onChange={handleInputChange} /><label htmlFor="data_policy_1"><p><span style={{ color: 'red' }}>*</span>I have read and agreed to the Terms and Conditions, and declared that all the information provided above are verified. 本人已閱讀和同意上述之推廣活動條款及細則，並聲明以上所提供的資料全均屬實。</p></label>
                <FormError field="data_policy_1" errors={validationErrors} />
          </div>
              <div className="box datapolicy">
                <input type="checkbox" id="data_policy_2" name="data_policy_2" value="Y" checked={formData.data_policy_2} onChange={handleInputChange} /><label htmlFor="data_policy_2"><p><span style={{ color: 'red' }}>*</span>I have read and agreed to the Personal Information Collection Statement. 本人已閱讀和同意上述之收集個人資料聲明。</p></label>
                <FormError field="data_policy_2" errors={validationErrors} />
              </div>
            </div>
            {/* Submit Section */}
          <div id="submit">
              <div className="box col_2" style={{ width: "100%" }}>
                <input type="text" id="checknum" name="checknum" maxLength={6} autoComplete="off" placeholder="驗證碼" value={formData.checknum} onChange={handleInputChange} className={getFieldClass('checknum')} />
                <FormError field="checknum" errors={validationErrors} />
              </div>
              <div className="box col_2 captcha" style={{ verticalAlign: 'bottom' }}>
                <div className="captcha-container">
                  <div className="captcha-code">{captchaCode}</div>
                  <button
                    type="button"
                    className="refresh-captcha"
                    onClick={() => generateCaptcha()}
                  >
                    用另一個圖像 Refresh Image
                  </button>
                  </div>
              </div>
              <span>Please refresh and use new verification code to re-submit the form if you have submitted application failed before.<br />若曾提交失敗，請於再次提交前刷新圖像並以新驗證碼重新提交其申請。</span>
              <br /><br />
              <div className="box submit-btn-wrap">
                <input
                  className="submit-btn"
                  type="submit"
                  name="submitbtn"
                  id="submitbtn"
                  value="Submit 遞交"
                />
              <div className="under-layer"></div>
              <div className="under-layer shadow"></div>
            </div>

            {/* Submission Error Display */}
            {submissionError && (
              <div className="submission-error" style={{
                marginTop: '15px',
                padding: '10px 15px',
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '4px',
                color: '#c62828',
                fontSize: '14px',
                lineHeight: '1.4',
                whiteSpace: 'pre-line'
              }}>
                <strong>Error:</strong> {submissionError}
              </div>
            )}
            <div className="instructions">
                Please note that applicant will receive an auto-reply email after submitting the application successfully. If you don't receive any reply email 3 days after your submission, please contact (+852) 2880 2978, or email <a href="mailto:smartcityawards@etnet.com.hk">smartcityawards@etnet.com.hk</a> .
                <br /><br />
                請注意如成功申請後應會收到一封自動回覆的電子郵件。<br />如閣下在3天内未能收到，請致電 (+852) 2880 2978，或電郵 <a href="mailto:smartcityawards@etnet.com.hk">smartcityawards@etnet.com.hk</a>
            </div>
          </div>
        </form>
        </div>
      </section>

    </div>
  );
};

export default Form2025;